import { Component, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { RequestService } from "@services/index";

export interface PrintLabelsDialogData {
  /** The orders to print labels for (the merchant's own parcels of each). */
  orderIds: string[];
}

/**
 * Print shipping labels: how many parcels (one label each, "1 of 2"...) and
 * on what paper, then the PDF opens in a new tab to print. The tab is opened
 * on the tap itself, so browsers do not block it as a pop-up.
 */
@Component({
  selector: "fh-print-labels-dialog",
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
  ],
  template: `
    <h2 mat-dialog-title>Print shipping label{{ data.orderIds.length > 1 ? "s" : "" }}</h2>
    <mat-dialog-content>
      <p class="hint">
        {{ data.orderIds.length }} order{{ data.orderIds.length > 1 ? "s" : "" }} - Singapore and US orders;
        walk-in items are left off. US parcels still need the carrier's postage label.
      </p>
      <mat-form-field appearance="outline" class="parcels">
        <mat-label>Parcels per order</mat-label>
        <input matInput type="number" min="1" max="20" [(ngModel)]="parcels" />
        <mat-hint>One label per box: "1 of 2", "2 of 2"</mat-hint>
      </mat-form-field>
      <p class="label">Paper</p>
      <mat-radio-group [(ngModel)]="paper" class="paper">
        <mat-radio-button value="label">Label printer - 100 x 150 mm</mat-radio-button>
        <mat-radio-button value="a4">A4 - one label per page</mat-radio-button>
      </mat-radio-group>
      @if (error()) {
        <p class="error">{{ error() }}</p>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancel</button>
      <button mat-flat-button color="primary" [disabled]="busy()" (click)="print()">
        {{ busy() ? "Preparing..." : "Print" }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .hint { color: #6b7280; font-size: 13px; margin-top: 0; }
      .parcels { width: 100%; margin-top: 8px; }
      .label { margin: 16px 0 4px; font-weight: 500; }
      .paper { display: flex; flex-direction: column; gap: 6px; }
      .error { color: #b91c1c; font-size: 13px; margin-top: 12px; }
    `,
  ],
})
export class PrintLabelsDialogComponent {
  parcels = 1;
  paper: "label" | "a4" = "label";
  busy = signal(false);
  error = signal("");

  readonly data = inject<PrintLabelsDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<PrintLabelsDialogComponent>);
  private requestService = inject(RequestService);

  print(): void {
    const parcels = Math.min(Math.max(Math.floor(Number(this.parcels)) || 1, 1), 20);
    // Opened now, on the tap, then pointed at the PDF once it is ready.
    const tab = window.open("", "_blank");
    this.busy.set(true);
    this.error.set("");
    const ids = this.data.orderIds.map(encodeURIComponent).join(",");
    this.requestService
      .getBlob(`/api/v2/shipping-labels?orderIds=${ids}&parcels=${parcels}&paper=${this.paper}`)
      .subscribe({
        next: async (blob) => {
          // The server answers HTTP 200 even when it refuses (no parcels, an
          // error), so a JSON reply lands here too: show its message instead.
          if (blob?.type !== "application/pdf") {
            this.busy.set(false);
            tab?.close();
            this.error.set(await this.messageOf(blob));
            return;
          }
          this.busy.set(false);
          const url = URL.createObjectURL(blob);
          if (tab) tab.location.href = url;
          else window.location.href = url;
          // Long enough for the tab to load the PDF, then free the blob.
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
          this.dialogRef.close(true);
        },
        error: async (err) => {
          this.busy.set(false);
          tab?.close();
          this.error.set(await this.messageOf(err?.error));
        },
      });
  }

  /** The server explains in JSON; it arrives as a blob here. */
  private async messageOf(blob: unknown): Promise<string> {
    const fallback = "The labels could not be made";
    try {
      const body = JSON.parse(await (blob as Blob).text());
      return body?.message || fallback;
    } catch {
      return fallback;
    }
  }
}
